import { useMemo } from 'react';
import {
  RichTextEditor,
  type RichTextEditorLabels,
} from '@amzn/gsce-shared-react-lib/components';

import { useMessages } from '../i18n/useMessages';

// ─── Types ───────────────────────────────────────────────────────────

const RTE_BUNDLE = 'shared.ui.translations.richTextEditor';

/** Default tracking id for callers that don't supply one (e.g. the audit
 *  manual-flow task description). Audit-whiz call sites pass a specific id. */
const DEFAULT_TRACKING_ID = 'shared.ui.rich-text-editor';

type RteMessageKey = keyof RichTextEditorLabels;

interface RichTextEditorFieldProps {
  readonly value: string;
  readonly onChange: (html: string) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly ariaLabel?: string;
  /** Id of the element describing this field (e.g. a FormField's error text),
   *  rendered as `aria-describedby` on the editor wrapper so a screen reader
   *  announces the error. (The lib RTE itself does not yet forward this — a
   *  follow-up will push it into the lib; for now the adapter owns the
   *  wrapper and carries it there.) */
  readonly ariaDescribedBy?: string;
  /** Shows the invalid visual state (critical-red border, `aria-invalid`),
   *  mapped to the lib RTE's `error` prop. */
  readonly invalid?: boolean;
  /** Change-tracking id logged by the lib RTE on every edit. Defaults to a
   *  generic id; audit-whiz fields pass a field-specific one. */
  readonly trackingId?: string;
  readonly dataTestId?: string;
}

// ─── Component ───────────────────────────────────────────────────────

/**
 * Thin i18n adapter over the shared-lib {@link RichTextEditor}. The library
 * owns all editor behavior — the critical-red error border (`error`), the
 * image-insert modal, change tracking, and external-value sync — so this
 * wrapper exists only to:
 *  - resolve the 16 toolbar/modal `labels` from the app's RTE i18n bundle in
 *    ONE place (the lib is i18n-bundle-agnostic), and
 *  - keep the app-facing `invalid`/`ariaDescribedBy` surface the call sites
 *    already use, mapping `invalid` → the lib's `error`.
 *
 * `aria-describedby` is rendered on the adapter's wrapper because the lib RTE
 * does not yet forward it; a follow-up adds that prop to the lib and this
 * adapter is deleted entirely.
 */
export function RichTextEditorField({
  value,
  onChange,
  placeholder,
  disabled = false,
  ariaLabel,
  ariaDescribedBy,
  invalid = false,
  trackingId = DEFAULT_TRACKING_ID,
  dataTestId,
}: RichTextEditorFieldProps): JSX.Element {
  const { t } = useMessages<RteMessageKey>(RTE_BUNDLE);

  // The caller-translated label set the lib RTE needs. `t` is referentially
  // stable (useMessages guarantee), so this memo stays stable across renders
  // and the lib's internal `controls` memo doesn't churn.
  const labels = useMemo(
    (): RichTextEditorLabels => ({
      rteBold: t('rteBold'),
      rteItalic: t('rteItalic'),
      rteUnderline: t('rteUnderline'),
      rteParagraph: t('rteParagraph'),
      rteHeading1: t('rteHeading1'),
      rteHeading2: t('rteHeading2'),
      rteHeading3: t('rteHeading3'),
      rteBulletList: t('rteBulletList'),
      rteOrderedList: t('rteOrderedList'),
      rteLink: t('rteLink'),
      rteImage: t('rteImage'),
      rteUndo: t('rteUndo'),
      rteRedo: t('rteRedo'),
      rteHighlight: t('rteHighlight'),
      rteTakePhoto: t('rteTakePhoto'),
      rteDragAndDrop: t('rteDragAndDrop'),
    }),
    [t],
  );

  return (
    <div
      data-testid={dataTestId}
      {...(ariaDescribedBy != null && { 'aria-describedby': ariaDescribedBy })}
    >
      <RichTextEditor
        trackingId={trackingId}
        value={value}
        onChange={onChange}
        labels={labels}
        error={invalid}
        {...(ariaLabel != null && { ariaLabel })}
        {...(placeholder != null && { placeholder })}
        disabled={disabled}
      />
    </div>
  );
}
